// Lightweight code formatters/minifiers (CSS, SQL, XML). Text-in, text-out,
// pure and browser-local. These are pragmatic transforms, not full parsers.

export type CodeToolVariant =
  | 'css-minifier'
  | 'css-beautifier'
  | 'sql-minifier'
  | 'xml-minifier'
  | 'html-beautifier'
  | 'javascript-minifier'
  | 'javascript-beautifier';

export const codeToolMeta: Record<
  CodeToolVariant,
  { badge: string; title: string; inputLabel: string; outputLabel: string; sample: string }
> = {
  'css-minifier': {
    badge: 'CSS',
    title: 'Minify CSS',
    inputLabel: 'CSS',
    outputLabel: 'Minified CSS',
    sample: '.card {\n  color: #333;\n  padding: 16px; /* comment */\n}',
  },
  'css-beautifier': {
    badge: 'CSS',
    title: 'Beautify CSS',
    inputLabel: 'CSS',
    outputLabel: 'Formatted CSS',
    sample: '.card{color:#333;padding:16px}',
  },
  'sql-minifier': {
    badge: 'SQL',
    title: 'Minify SQL',
    inputLabel: 'SQL',
    outputLabel: 'Minified SQL',
    sample: 'SELECT id, name\nFROM users\nWHERE active = 1;',
  },
  'xml-minifier': {
    badge: 'XML',
    title: 'Minify XML',
    inputLabel: 'XML',
    outputLabel: 'Minified XML',
    sample: '<root>\n  <item>one</item>\n  <item>two</item>\n</root>',
  },
  'html-beautifier': {
    badge: 'HTML',
    title: 'Beautify HTML',
    inputLabel: 'HTML',
    outputLabel: 'Formatted HTML',
    sample: '<ul><li>One</li><li>Two</li></ul>',
  },
  'javascript-minifier': {
    badge: 'JS',
    title: 'Minify JavaScript',
    inputLabel: 'JavaScript',
    outputLabel: 'Minified JavaScript',
    sample: 'function add(a, b) {\n  // sum two numbers\n  return a + b;\n}',
  },
  'javascript-beautifier': {
    badge: 'JS',
    title: 'Beautify JavaScript',
    inputLabel: 'JavaScript',
    outputLabel: 'Formatted JavaScript',
    sample: 'function add(a,b){return a+b;}const x=add(1,2);',
  },
};

// ---------------------------------------------------------------------------
// Scanners. A minifier that is not aware of strings, regex literals and comments
// does not shrink code, it corrupts it. Each of these walks the text once and
// knows what it is standing inside.
// ---------------------------------------------------------------------------

type JsPiece =
  | { kind: 'code'; text: string }
  | { kind: 'string'; text: string }
  | { kind: 'regex'; text: string }
  | { kind: 'comment'; text: string; line: boolean };

// A slash is a regex literal only where a value can start. After an identifier,
// a number, or a closing bracket, it is division.
function slashStartsRegex(previous: string): boolean {
  const trimmed = previous.replace(/\s+$/, '');
  if (!trimmed) return true;
  const last = trimmed[trimmed.length - 1];
  if (/[a-zA-Z0-9_$)\]]/.test(last)) {
    const word = trimmed.match(/[a-zA-Z_$][\w$]*$/)?.[0];
    // `return /x/` is a regex; `a / b` is division.
    return Boolean(
      word &&
      ['return', 'typeof', 'instanceof', 'in', 'of', 'new', 'delete', 'void', 'case'].includes(
        word,
      ),
    );
  }
  return true;
}

function scanJsPieces(js: string): JsPiece[] {
  const pieces: JsPiece[] = [];
  let code = '';
  let i = 0;
  const push = () => {
    if (code) {
      pieces.push({ kind: 'code', text: code });
      code = '';
    }
  };

  while (i < js.length) {
    const char = js[i];
    const next = js[i + 1];

    if (char === '/' && next === '/') {
      const end = js.indexOf('\n', i);
      const stop = end === -1 ? js.length : end;
      push();
      pieces.push({ kind: 'comment', text: js.slice(i, stop), line: true });
      i = stop;
      continue;
    }

    if (char === '/' && next === '*') {
      const end = js.indexOf('*/', i + 2);
      const stop = end === -1 ? js.length : end + 2;
      push();
      pieces.push({ kind: 'comment', text: js.slice(i, stop), line: false });
      i = stop;
      continue;
    }

    if (char === '"' || char === "'" || char === '`') {
      const quote = char;
      let j = i + 1;
      while (j < js.length) {
        if (js[j] === '\\') {
          j += 2;
          continue;
        }
        if (js[j] === quote) break;
        j += 1;
      }
      push();
      pieces.push({ kind: 'string', text: js.slice(i, Math.min(j + 1, js.length)) });
      i = j + 1;
      continue;
    }

    if (char === '/' && slashStartsRegex(code)) {
      let j = i + 1;
      let inClass = false;
      let closed = false;
      while (j < js.length) {
        const c = js[j];
        if (c === '\\') {
          j += 2;
          continue;
        }
        if (c === '\n') break;
        if (c === '[') inClass = true;
        else if (c === ']') inClass = false;
        else if (c === '/' && !inClass) {
          closed = true;
          break;
        }
        j += 1;
      }
      if (closed) {
        let end = j + 1;
        while (end < js.length && /[a-z]/i.test(js[end])) end += 1;
        push();
        pieces.push({ kind: 'regex', text: js.slice(i, end) });
        i = end;
        continue;
      }
    }

    code += char;
    i += 1;
  }
  push();
  return pieces;
}

function minifyJs(js: string): string {
  const pieces = scanJsPieces(js);
  let out = '';

  for (const piece of pieces) {
    if (piece.kind === 'comment') {
      // A line comment ends at a newline that may be carrying an ASI. Keep the newline.
      if (piece.line) out += '\n';
      continue;
    }
    if (piece.kind === 'string' || piece.kind === 'regex') {
      out += piece.text;
      continue;
    }

    // Collapse indentation, but never delete a line break: without semicolons,
    // the newline IS the statement terminator.
    out += piece.text
      .replace(/[^\S\n]+/g, ' ')
      .replace(/ *\n[ \n]*/g, '\n')
      .replace(/\s*([{};,:()])\s*/g, (match, symbol) =>
        match.includes('\n') && (symbol === '}' || symbol === '{') ? symbol : symbol,
      );
  }

  return out
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .join('\n')
    .trim();
}

function beautifyJs(js: string): string {
  // Beautifying by minifying first is how every comment got deleted.
  const pieces = scanJsPieces(js);
  let out = '';
  let depth = 0;
  const pad = () => '  '.repeat(Math.max(depth, 0));
  const trimEnd = () => {
    out = out.replace(/[^\S\n]+$/, '');
  };

  for (const piece of pieces) {
    if (piece.kind === 'comment') {
      trimEnd();
      if (out && !out.endsWith('\n')) out += '\n' + pad();
      out += piece.text;
      out += '\n' + pad();
      continue;
    }
    if (piece.kind === 'string' || piece.kind === 'regex') {
      out += piece.text;
      continue;
    }

    for (const char of piece.text) {
      if (char === '{') {
        depth += 1;
        trimEnd();
        out += ' {\n' + pad();
      } else if (char === '}') {
        depth = Math.max(depth - 1, 0);
        out = out.replace(/\s+$/, '');
        out += '\n' + pad() + '}';
        out += '\n' + pad();
      } else if (char === ';') {
        trimEnd();
        out += ';\n' + pad();
      } else if (char === '\n' || char === '\r') {
        // handled by the structure above
      } else if (/\s/.test(char)) {
        if (!out.endsWith(' ') && !out.endsWith('\n')) out += ' ';
      } else {
        out += char;
      }
    }
  }

  return out
    .split('\n')
    .map((line) => line.replace(/\s+$/, ''))
    .filter((line, index, all) => line.trim() !== '' || (index > 0 && all[index - 1].trim() !== ''))
    .join('\n')
    .trim();
}

// --- CSS ---------------------------------------------------------------------

type CssPiece =
  | { kind: 'code'; text: string }
  | { kind: 'string'; text: string }
  | { kind: 'comment'; text: string };

function scanCssPieces(css: string): CssPiece[] {
  const pieces: CssPiece[] = [];
  let code = '';
  let i = 0;
  const push = () => {
    if (code) {
      pieces.push({ kind: 'code', text: code });
      code = '';
    }
  };

  while (i < css.length) {
    const char = css[i];
    if (char === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2);
      const stop = end === -1 ? css.length : end + 2;
      push();
      pieces.push({ kind: 'comment', text: css.slice(i, stop) });
      i = stop;
      continue;
    }
    if (char === '"' || char === "'") {
      const quote = char;
      let j = i + 1;
      while (j < css.length) {
        if (css[j] === '\\') {
          j += 2;
          continue;
        }
        if (css[j] === quote) break;
        j += 1;
      }
      push();
      pieces.push({ kind: 'string', text: css.slice(i, Math.min(j + 1, css.length)) });
      i = j + 1;
      continue;
    }
    code += char;
    i += 1;
  }
  push();
  return pieces;
}

function minifyCss(css: string): string {
  const pieces = scanCssPieces(css);
  const parked: string[] = [];
  let out = '';

  for (const piece of pieces) {
    if (piece.kind === 'comment') continue;
    if (piece.kind === 'string') {
      // A quoted string is content. "Hello, world" is one value, not two, and the
      // structural passes below must not see inside it.
      parked.push(piece.text);
      out += `cssstr${parked.length - 1}`;
      continue;
    }

    for (const char of piece.text) {
      if (/\s/.test(char)) {
        if (!out.endsWith(' ')) out += ' ';
        continue;
      }
      out += char;
    }
  }

  // Tighten around structural characters one block at a time, because a colon
  // glues inside a declaration but separates in a selector: "a :hover" and
  // "a:hover" are different rules.
  let inBlock = false;
  const rebuilt = out
    .split(/(\{|\})/)
    .map((chunk) => {
      if (chunk === '{') {
        inBlock = true;
        return '{';
      }
      if (chunk === '}') {
        inBlock = false;
        return '}';
      }
      let text = chunk.replace(/\s*([;,])\s*/g, '$1');
      if (inBlock) text = text.replace(/\s*:\s*/g, ':');
      return text.replace(/\s+/g, ' ').trim();
    })
    .join('');

  return rebuilt
    .replace(/;}/g, '}')
    .replace(/cssstr(\d+)/g, (_, index) => parked[Number(index)])
    .trim();
}

function beautifyCss(css: string): string {
  const pieces = scanCssPieces(css);
  let out = '';
  let depth = 0;
  const pad = () => '  '.repeat(Math.max(depth, 0));

  for (const piece of pieces) {
    // Comments are kept: a beautifier that deletes them is a minifier.
    if (piece.kind === 'comment') {
      out = out.replace(/[^\S\n]+$/, '');
      if (out && !out.endsWith('\n')) out += '\n' + pad();
      out += piece.text + '\n' + pad();
      continue;
    }
    if (piece.kind === 'string') {
      out += piece.text;
      continue;
    }

    for (const char of piece.text) {
      if (char === '{') {
        depth += 1;
        out = out.replace(/\s+$/, '');
        out += ' {\n' + pad();
      } else if (char === '}') {
        depth = Math.max(depth - 1, 0);
        out = out.replace(/\s+$/, '');
        out += '\n' + pad() + '}\n' + pad();
      } else if (char === ';') {
        out = out.replace(/[^\S\n]+$/, '');
        out += ';\n' + pad();
      } else if (char === ':') {
        out += ': ';
      } else if (/\s/.test(char)) {
        if (!out.endsWith(' ') && !out.endsWith('\n')) out += ' ';
      } else {
        out += char;
      }
    }
  }

  return out
    .split('\n')
    .map((line) => line.replace(/\s+$/, ''))
    .filter((line, index, all) => line.trim() !== '' || (index > 0 && all[index - 1].trim() !== ''))
    .join('\n')
    .replace(/:\s+;/g, ';')
    .trim();
}

// --- SQL ---------------------------------------------------------------------

function minifySql(sql: string): string {
  let out = '';
  let i = 0;

  while (i < sql.length) {
    const char = sql[i];

    // A double dash inside a string literal is text, not a comment. Stripping it
    // with a regex truncated the rest of the statement.
    if (char === "'" || char === '"' || char === '`') {
      const quote = char;
      let j = i + 1;
      while (j < sql.length) {
        if (sql[j] === quote && sql[j + 1] === quote) {
          j += 2;
          continue;
        }
        if (sql[j] === '\\' && quote === "'") {
          j += 2;
          continue;
        }
        if (sql[j] === quote) break;
        j += 1;
      }
      out += sql.slice(i, Math.min(j + 1, sql.length));
      i = j + 1;
      continue;
    }

    if (char === '-' && sql[i + 1] === '-') {
      const end = sql.indexOf('\n', i);
      i = end === -1 ? sql.length : end;
      out += ' ';
      continue;
    }

    if (char === '/' && sql[i + 1] === '*') {
      const end = sql.indexOf('*/', i + 2);
      i = end === -1 ? sql.length : end + 2;
      out += ' ';
      continue;
    }

    if (/\s/.test(char)) {
      if (!out.endsWith(' ')) out += ' ';
      i += 1;
      continue;
    }

    out += char;
    i += 1;
  }

  return out
    .replace(/ *([(),]) */g, '$1 ')
    .replace(/ *; */g, '; ')
    .replace(/\s+/g, ' ')
    .replace(/\s+;/g, ';')
    .trim();
}

// --- XML ---------------------------------------------------------------------

function minifyXml(xml: string): string {
  // Whitespace between tags is layout. Whitespace inside mixed content is a word
  // gap, and collapsing it ran "Hello <b>bold</b> tail" into "Hellobold tail".
  const parked: string[] = [];
  const guarded = xml.replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, (match) => {
    parked.push(match);
    return `cdata${parked.length - 1}`;
  });

  const minified = guarded
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/>\s+</g, '><')
    .replace(/[ \t]*\n[ \t\n]*/g, ' ')
    .trim();

  return minified.replace(/cdata(\d+)/g, (_, index) => parked[Number(index)]);
}

function beautifyHtml(html: string): string {
  // Void elements never get a closing tag, so they must not change indent depth.
  const voidTags = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);
  const tokens = html
    .replace(/>\s+</g, '><')
    .replace(/</g, '\n<')
    .replace(/>/g, '>\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  let depth = 0;
  const out: string[] = [];
  for (const token of tokens) {
    const isClosing = /^<\//.test(token);
    const tagName = (token.match(/^<\/?\s*([a-zA-Z0-9-]+)/) || [])[1]?.toLowerCase() ?? '';
    const isSelfClosing = /\/>$/.test(token) || voidTags.has(tagName);
    const isOpening = /^<[a-zA-Z]/.test(token) && !isSelfClosing;
    if (isClosing) depth = Math.max(depth - 1, 0);
    out.push('  '.repeat(depth) + token);
    if (isOpening) depth += 1;
  }
  return out.join('\n');
}

export function runCodeTool(variant: CodeToolVariant, input: string): string {
  if (!input.trim()) return '';
  if (variant === 'css-minifier') return minifyCss(input);
  if (variant === 'css-beautifier') return beautifyCss(input);
  if (variant === 'sql-minifier') return minifySql(input);
  if (variant === 'html-beautifier') return beautifyHtml(input);
  if (variant === 'javascript-minifier') return minifyJs(input);
  if (variant === 'javascript-beautifier') return beautifyJs(input);
  return minifyXml(input);
}
